import type { GetServerSidePropsContext } from 'next'
import Cookie from 'cookie-universal'
import shuffle from 'lodash/shuffle'
import { useBucket } from '@mc/experiments-react'

export const useEnrollmentDate = () => {
  // https://app.growthbook.io/features/certificates-enrollment-date
  const date = useBucket('certificates-enrollment-date')
  return date
}

export const CERTIFICATE_SLUGS = [
  'data-science-foundations',
  'data-science-for-business',
  'storytelling-with-data',
  'cybersecurity',
  'digital-transformation',
  'emerging-technologies',
  'creative-problem-solving',
  'entrepreneurship',
  'growth-strategy',
  'innovation-management',
  'budgeting-&-planning',
  'finance-foundations',
  'investment-analysis',
  'complex-decision-making',
  'leadership',
  'negotiation-&-influence',
  'storytelling-in-business',
  'strategic-leadership',
  'strategic-planning',
  'team-leadership',
  'brand-marketing',
  'growth-marketing',
  'marketing-foundations',
  'sales-leadership',
  'strategic-sales',
  'product-design',
  'product-management',
  'product-marketing',
  'project-mgmt-leadership',
  'project-management',
  'technical-program-mgmt',
  'influence-&-power',
  'strategic-marketing',
]

export type Certificate = (typeof CERTIFICATE_SLUGS)[number]

export interface CertificateInfo {
  title: string
  titleExtended: string
  slug: Certificate
  productId: number
  productName: string
  category: string
  availableSoon?: boolean
}

export interface LatestCertificateInfo {
  title: string
  slug: string
  thumbnail16x9: { url: string }
  availableSoon?: boolean
}

export const CERTIFICATES: CertificateInfo[] = [
  {
    title: 'Data Science Foundations',
    titleExtended: 'Data Science: Foundations for Strategic Decision-Making',
    slug: 'data-science-foundations',
    productId: 79,
    productName: 'MC Certificates - Data Science Foundations',
    category: 'Data Science & Analytics',
  },
  {
    title: 'Data Science for Business',
    titleExtended: 'Data Science Models & Their Business Applications',
    slug: 'data-science-for-business',
    productId: 80,
    productName: 'MC Certificates - Data Science for Business',
    category: 'Data Science & Analytics',
    availableSoon: true,
  },
  {
    title: 'Storytelling with Data',
    titleExtended: 'Data Visualization and Storytelling: Making Data Speak',
    slug: 'storytelling-with-data',
    productId: 81,
    productName: 'MC Certificates - Storytelling with Data',
    category: 'Data Science & Analytics',
    availableSoon: true,
  },
  {
    title: 'Cybersecurity',
    titleExtended: 'Cybersecurity: Securing the Modern Enterprise',
    slug: 'cybersecurity',
    productId: 82,
    productName: 'MC Certificates - Cybersecurity',
    category: 'Digital Transformation',
  },
  {
    title: 'Digital Transformation',
    titleExtended: 'Digital Leadership: Leading in a Digital World',
    slug: 'digital-transformation',
    productId: 83,
    productName: 'MC Certificates - Digital Transformation',
    category: 'Digital Transformation',
  },
  {
    title: 'Emerging Technologies',
    titleExtended: 'Emerging Technologies: Shaping the Future of Business',
    slug: 'emerging-technologies',
    productId: 84,
    productName: 'MC Certificates - Emerging Technologies',
    category: 'Digital Transformation',
  },
  {
    title: 'Creative Problem Solving',
    titleExtended: 'Design Thinking: Creative Approaches for Modern Challenges',
    slug: 'creative-problem-solving',
    productId: 85,
    productName: 'MC Certificates - Creative Problem Solving',
    category: 'Entrepreneurship & Innovation',
  },
  {
    title: 'Entrepreneurship',
    titleExtended: 'Entrepreneurship: Launching and Scaling New Ventures',
    slug: 'entrepreneurship',
    productId: 86,
    productName: 'MC Certificates - Entrepreneurship',
    category: 'Entrepreneurship & Innovation',
  },
  {
    title: 'Growth Strategy',
    titleExtended: 'Growth Strategy: Expanding Business ',
    slug: 'growth-strategy',
    productId: 87,
    productName: 'MC Certificates - Growth Strategy',
    category: 'Entrepreneurship & Innovation',
  },
  {
    title: 'Innovation Management',
    titleExtended: 'Innovation Management: Driving Creativity and Change',
    slug: 'innovation-management',
    productId: 88,
    productName: 'MC Certificates - Innovation Management',
    category: 'Entrepreneurship & Innovation',
  },
  {
    title: 'Budgeting & Planning',
    titleExtended: 'Budgeting and Planning: Financial Success for Business',
    slug: 'budgeting-&-planning',
    productId: 89,
    productName: 'MC Certificates - Budgeting & Planning',
    category: 'Finance',
  },
  {
    title: 'Finance Foundations',
    titleExtended:
      'Finance Fundamentals: Building a Strong Financial Foundation',
    slug: 'finance-foundations',
    productId: 90,
    productName: 'MC Certificates - Finance Foundations',
    category: 'Finance',
  },
  {
    title: 'Investment Analysis',
    titleExtended: 'Investment Analysis: Making Data-Driven Investment Choices',
    slug: 'investment-analysis',
    productId: 91,
    productName: 'MC Certificates - Investment Analysis',
    category: 'Finance',
  },
  {
    title: 'Complex Decision Making',
    titleExtended: 'Decision-Making: Navigating Complex Business Challenges',
    slug: 'complex-decision-making',
    productId: 92,
    productName: 'MC Certificates - Complex Decision Making',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Leadership',
    titleExtended:
      'Leadership Essentials: Developing Personal Leadership Skills',
    slug: 'leadership',
    productId: 93,
    productName: 'MC Certificates - Leadership',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Negotiation & Influence',
    titleExtended: 'Negotiation: Achieving Win-Win Outcomes',
    slug: 'negotiation-&-influence',
    productId: 94,
    productName: 'MC Certificates - Negotiation & Influence',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Storytelling in Business',
    titleExtended: 'Business Storytelling: Persuading and Inspiring Audiences',
    slug: 'storytelling-in-business',
    productId: 95,
    productName: 'MC Certificates - Storytelling in Business',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Strategic Leadership',
    titleExtended: 'Strategic Leadership: Visionary Leadership for Growth',
    slug: 'strategic-leadership',
    productId: 96,
    productName: 'MC Certificates - Strategic Leadership',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Strategic Planning',
    titleExtended: 'Strategic Planning: Creating Long-Term Success',
    slug: 'strategic-planning',
    productId: 97,
    productName: 'MC Certificates - Strategic Planning',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Team Leadership',
    titleExtended: 'Team Leadership: Leading High-Performance Teams',
    slug: 'team-leadership',
    productId: 98,
    productName: 'MC Certificates - Team Leadership',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Brand Marketing',
    titleExtended:
      'Brand Management: Building and Sustaining Market Leadership',
    slug: 'brand-marketing',
    productId: 99,
    productName: 'MC Certificates - Brand Marketing',
    category: 'Marketing & Sales',
  },
  {
    title: 'Growth Marketing',
    titleExtended: 'Marketing Analytics: Data-Driven Marketing for Results',
    slug: 'growth-marketing',
    productId: 100,
    productName: 'MC Certificates - Growth Marketing',
    category: 'Marketing & Sales',
  },
  {
    title: 'Marketing Foundations',
    titleExtended:
      'Marketing Fundamentals: Building and Implementing Strategies',
    slug: 'marketing-foundations',
    productId: 101,
    productName: 'MC Certificates - Marketing Foundations',
    category: 'Marketing & Sales',
  },
  {
    title: 'Sales Leadership',
    titleExtended: 'Sales Leadership: Leading Teams to Achieve Excellence',
    slug: 'sales-leadership',
    productId: 102,
    productName: 'MC Certificates - Sales Leadership',
    category: 'Marketing & Sales',
  },
  {
    title: 'Strategic Sales',
    titleExtended: 'Sales Strategy: Driving Growth Through Strategic Sales',
    slug: 'strategic-sales',
    productId: 103,
    productName: 'MC Certificates - Strategic Sales',
    category: 'Marketing & Sales',
  },
  {
    title: 'Influence & Power',
    titleExtended: 'Influence and Power: Strategies for Professional Impact',
    slug: 'influence-&-power',
    productId: 104,
    productName: 'MC Certificates - Influence & Power',
    category: 'Leadership & Strategy',
  },
  {
    title: 'Product Design',
    titleExtended: 'Product Design: Crafting Solutions for Customer Needs',
    slug: 'product-design',
    productId: 105,
    productName: 'MC Certificates - Product Design',
    category: 'Product & Design',
  },
  {
    title: 'Product Management',
    titleExtended:
      'Product Management: Driving Success Across Product Lifecycles',
    slug: 'product-management',
    productId: 106,
    productName: 'MC Certificates - Product Management',
    category: 'Product & Design',
  },
  {
    title: 'Product Marketing',
    titleExtended: 'Product Marketing: Aligning Product with Market Needs',
    slug: 'product-marketing',
    productId: 107,
    productName: 'MC Certificates - Product Marketing',
    category: 'Product & Design',
  },
  {
    title: 'Project Mgmt Leadership',
    titleExtended: 'Project Leadership: Managing Teams for Successful Outcomes',
    slug: 'project-mgmt-leadership',
    productId: 108,
    productName: 'MC Certificates - Project Mgmt Leadership',
    category: 'Project Management',
  },
  {
    title: 'Project Management',
    titleExtended: 'Project Management: Delivering Projects on Time and Budget',
    slug: 'project-management',
    productId: 109,
    productName: 'MC Certificates - Project Management',
    category: 'Project Management',
  },
  {
    title: 'Technical Program Mgmt',
    titleExtended: 'Technical Program Management: Leading Complex Programs',
    slug: 'technical-program-mgmt',
    productId: 110,
    productName: 'MC Certificates - Technical Program Mgmt',
    category: 'Project Management',
  },
  {
    title: 'Strategic Marketing',
    titleExtended:
      'Strategic Marketing: Aligning Goals with Market Opportunities',
    slug: 'strategic-marketing',
    productId: 111,
    productName: 'MC Certificates - Strategic Marketing',
    category: 'Marketing & Sales',
  },
]

export const LATEST_CERTIFICATES: LatestCertificateInfo[] = [
  {
    title: 'Leadership and Decision-Making Under Pressure',
    slug: 'leadership-and-decision-making-under-pressure',
    thumbnail16x9: {
      url: 'https://static.masterclass.com/nsf-promo-modal-image-16x9-v2.jpg',
    },
  },
  {
    title: 'Breakthrough Innovation Strategy',
    slug: 'breakthrough-innovation-strategy-from-bold-idea-to-business-impact',
    thumbnail16x9: {
      url: 'https://static.masterclass.com/nsf-promo-modal-image-16x9-v2.jpg',
    },
  },
  {
    title: 'Scaling AI Strategy',
    slug: 'scaling-ai-strategy-build-launch-and-drive-what-s-next',
    thumbnail16x9: {
      url: 'https://static.masterclass.com/nsf-promo-modal-image-16x9-v2.jpg',
    },
    availableSoon: true,
  },
  {
    title: 'Lead With AI',
    slug: 'lead-with-ai-adapt-implement-transform-your-organization',
    thumbnail16x9: {
      url: 'https://static.masterclass.com/nsf-promo-modal-image-16x9-v2.jpg',
    },
    availableSoon: true,
  },
]

const restoreCertificatesOrder = (prevOrder: string[]): CertificateInfo[] => {
  const certificates = prevOrder
    .map((orderedCert) =>
      CERTIFICATES.find((cert) => cert.slug === orderedCert),
    )
    .filter(Boolean) as CertificateInfo[]

  return certificates
}

type Req = GetServerSidePropsContext['req']
type Res = GetServerSidePropsContext['res']
// The cookie will likely not be needed once we have the final data.
const CERTIFICATES_ORDER_COOKIE = 'mc_certificates_ordered_v3'

const getCertificatesOrderCookie = (req: Req, res: Res) => {
  const cookies = Cookie(req, res)
  return cookies.get(CERTIFICATES_ORDER_COOKIE) || null
}

const setCertificatesOrderCookie = (
  req: Req,
  res: Res,
  certificatesOrder: string[],
): void => {
  const cookies = Cookie(req, res)
  cookies.set(CERTIFICATES_ORDER_COOKIE, JSON.stringify(certificatesOrder), {
    maxAge: 60 * 60 * 24 * 30, // 1 month
    path: '/',
  })
}

// Get shuffled certificates, restoring order from cookies if available
export const getShuffledCertificates = (
  ctx: GetServerSidePropsContext,
): CertificateInfo[] => {
  const { req, res } = ctx
  const savedOrder = getCertificatesOrderCookie(req, res)

  if (savedOrder && Array.isArray(savedOrder)) {
    return restoreCertificatesOrder(savedOrder)
  }

  const newOrder = shuffle(CERTIFICATE_SLUGS)
  setCertificatesOrderCookie(req, res, newOrder)

  return restoreCertificatesOrder(newOrder)
}

export const getLatestCertificates = (): LatestCertificateInfo[] =>
  LATEST_CERTIFICATES
